/**
 * Feedback words for the admin side (the inbox, a thread, a user's list).
 *
 * The stored values never change: six `suggestion_type`s (plus legacy's
 * `bug`) and four statuses. Admin reads the stored status in its own words
 * (New, In progress, Done, Won't fix), because an admin triages by it; the
 * chef's side (features/feedback) shows the same four as Open / In progress
 * / Done / Won't fix.
 *
 * Colour law: a status is a state, never money, so the chips are ink and
 * neutral only. The loudest chip is the one that needs you: New is the ink
 * fill, In progress the sunk fill, Done an ink outline, Won't fix the quiet
 * outline. The word always carries the state; the chip only helps a scan.
 */
import type { StatusBadgeProps } from 'pleach-ds';

export type FeedbackStatus = 'new' | 'in_progress' | 'done' | 'wontfix';
export const FEEDBACK_STATUSES: FeedbackStatus[] = ['new', 'in_progress', 'done', 'wontfix'];

export const STATUS_LABEL: Record<string, string> = {
  new: 'New',
  in_progress: 'In progress',
  done: 'Done',
  wontfix: "Won't fix",
};

/** The inbox filter: a status, or needs_reply, the threads with a chef's
 *  reply no admin has opened yet (whatever their status). */
export type FeedbackFilter = FeedbackStatus | 'needs_reply';
export const FEEDBACK_FILTERS: FeedbackFilter[] = ['new', 'needs_reply', 'in_progress', 'done', 'wontfix'];
export const FILTER_LABEL: Record<'needs_reply', string> = { needs_reply: 'Needs a reply' };

const STATUS_CHIP: Record<string, { kind: NonNullable<StatusBadgeProps['kind']>; title: string }> = {
  new: { kind: 'shipped', title: 'New: nobody has picked this up yet' },
  in_progress: { kind: 'planned', title: 'In progress' },
  done: { kind: 'replied', title: 'Done' },
  wontfix: { kind: 'open', title: "Won't fix" },
};

/** The StatusBadge props for a stored feedback status. */
export function statusChip(status: string): { kind: NonNullable<StatusBadgeProps['kind']>; label: string; title: string } {
  const chip = STATUS_CHIP[status] ?? { kind: 'open' as const, title: status };
  return { ...chip, label: STATUS_LABEL[status] ?? status.replace(/_/g, ' ') };
}

/** The inbox's one-tap step for a status, or null when there is none:
 *  New moves to In progress, In progress to Done. `done` is the toast. */
export function nextStep(status: string): { status: FeedbackStatus; label: string; done: string } | null {
  if (status === 'new') return { status: 'in_progress', label: 'Mark in progress', done: 'Marked in progress.' };
  if (status === 'in_progress') return { status: 'done', label: 'Mark done', done: 'Marked done.' };
  return null;
}

/** One or two words per type, as the handoff's admin table writes them
 *  ("Bug", "Idea"). `bug` is legacy's name for bug_report. */
export const TYPE_LABEL: Record<string, string> = {
  bug_report: 'Bug',
  bug: 'Bug',
  feature_request: 'Idea',
  support: 'Question',
  too_complicated: 'Unclear',
  missing_content: 'Missing content',
  other: 'Other',
};

export function typeLabel(type: string): string {
  return TYPE_LABEL[type] ?? 'Other';
}

/** Visible text of a rich body, block ends as line breaks, entities decoded. */
export function htmlToText(html: string): string {
  const withBreaks = html.replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, '\n');
  if (typeof DOMParser !== 'undefined') {
    return new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent ?? '';
  }
  return withBreaks.replace(/<[^>]+>/g, '');
}

/** A one-line preview of a rich body: tags gone, whitespace collapsed, cut
 *  at `max` with an ellipsis. For lists only; a thread renders the real
 *  formatting through sanitizeHtml. */
export function textPreview(html: string, max = 200): string {
  const collapsed = htmlToText(html).replace(/\s+/g, ' ').trim();
  return collapsed.length > max ? `${collapsed.slice(0, max).trimEnd()}…` : collapsed;
}

const SUBJECT_RE = /^\s*<p>\s*<(strong|b)>([\s\S]*?)<\/\1>\s*<\/p>/i;

/** A stored body split the way the server splits it (services/feedback.py
 *  split_subject): the bold first paragraph the Tell us form writes is the
 *  subject, the rest is the message. No bold opening paragraph, or an empty
 *  one, means no subject and the whole body. */
export function splitSubject(html: string): { subject: string | null; body: string } {
  const m = SUBJECT_RE.exec(html);
  const subject = m ? htmlToText(m[2]).trim() : '';
  if (!m || !subject) return { subject: null, body: html };
  return { subject, body: html.slice(m[0].length) };
}

const cut = (line: string, max: number) => (line.length > max ? `${line.slice(0, max).trimEnd()}…` : line);

/** A thread's title: the bold subject line the Feedback form puts first,
 *  else the first line of text, cut at `max`. */
export function threadTitle(html: string, max = 90): string {
  const line =
    splitSubject(html).subject ||
    htmlToText(html)
      .split('\n')
      .map((l) => l.trim())
      .find(Boolean) ||
    '';
  return line ? cut(line, max) : 'No subject';
}

/** A list row's title from the inbox's plain fields: the subject the chef
 *  gave, else the opening words of the plain-text preview (the quick widget
 *  and older threads have no subject). The preview is plain text, so it is
 *  never parsed as HTML. */
export function rowTitle(subject: string | null | undefined, preview: string, max = 90): string {
  const line = subject?.trim() || preview.replace(/\s+/g, ' ').trim();
  return line ? cut(line, max) : 'No subject';
}

/** "1 new reply", "2 new replies": a chef's follow-ups the admin has not
 *  opened yet, the same words the chef's own thread list uses. */
export function newReplies(n: number): string {
  return `${n} ${n === 1 ? 'new reply' : 'new replies'}`;
}
